import React from 'react';
import {useShallow} from 'zustand/shallow';
import translate from '@/utils/translate';
import assertUnreachable from '@/utils/assertUnreachable';
import {STEPS} from './onboardingSteps';
import {useOnboardingStore} from './OnboardingStore';
import {WizardLayoutProps} from '@/components/wizard-layout/WizardLayout';
import {InstagramInstructionsMedia} from './components/connect-platforms/InstagramInstructions';
import PortfolioLink from '@/views/onboarding/components/portfolio-link';
import {OnboardingCompletionTopHeader} from './steps/OnboardingCompletion';
import {useUserData} from '@/hooks/useUserData';
import {IOS_APP_RELEASE_DATE} from './onboardingConstants';

type StepData = Partial<WizardLayoutProps>;

function useStepData(): StepData {
  const [step, firstName] = useOnboardingStore(
    useShallow((state) => [state.step, state.firstName])
  );

  const {user} = useUserData({});

  return React.useMemo(() => {
    switch (step) {
      case STEPS.NAME_AND_BIRTHDATE:
        return {
          heading: translate('Tell us a bit about yourself'),
        };
      case STEPS.CREATOR_SEGMENT:
        return {
          heading: translate('{name}, how do you collaborate with brands?', {
            name: firstName,
          }),
          wizardFooterButtonProps: {
            hideNextButton: true,
          },
        };
      case STEPS.LOCATION:
        return {
          heading: translate('Where do you live?'),
          subheading: translate('So we can find you local collaborations'),
        };
      case STEPS.GENDER:
        return {
          heading: translate('How do you identify?'),
          wizardFooterButtonProps: {
            hideNextButton: true,
          },
        };
      case STEPS.SOCIAL_APPS:
        return {
          heading: translate('Which socials are you on?'),
        };
      case STEPS.BEST_COLLABS:
        return {
          heading: translate('Which brands are you most excited to work with?'),
          subheading: translate('Choose up to 3'),
        };
      case STEPS.SPECIALTIES:
        return {
          heading: translate('What industries do you specialize in?'),
          subheading: translate('Choose your top 3'),
        };
      case STEPS.CAPABILITIES:
        return {
          heading: translate('What are your top skills?'),
          subheading: translate(
            'Choose as many as you want to highlight in your digital portfolio'
          ),
        };
      case STEPS.SYNC_PLATFORMS:
        return {
          heading: translate('Ready to sync your socials?'),
        };
      case STEPS.COLLABORATION_RATE:
        return {
          heading: translate('What’s your starting rate?'),
          subheading: translate(
            'Choose the minimum you’d like to earn per gig. Set it to $0 to access every opportunity.'
          ),
        };
      case STEPS.PORTFOLIO:
        return {
          heading: translate('What do you want to highlight?'),
          subheading: translate(
            'Choose up to 3 for now and update them anytime you want'
          ),
          wizardFooterButtonProps: {
            label: translate('Save & continue'),
          },
          renderFooter: () => <PortfolioLink />,
        };
      case STEPS.BIO:
        return {
          heading: translate('Do you have a Bio already?'),
          subheading: translate(
            'If not, we’ve added one for you to get the most out of your profile.'
          ),
        };
      case STEPS.COMPLETION:
        return {
          wizardFooterButtonProps: {
            label: translate('Explore gigs'),
          },
          progress: null,
          renderTopHeader() {
            return <OnboardingCompletionTopHeader />;
          },
        };
      case STEPS.INSTAGRAM_INSTRUCTIONS:
        return {
          heading: translate('Connect Your Instagram Account'),
          progress: null,
          renderTopHeader: InstagramInstructionsMedia,
          wizardFooterButtonProps: {
            label: translate('Continue with Facebook'),
            iconName: 'Social-Facebook',
          },
        };
      case STEPS.REONBOARDING: {
        const userSeenOnboarding =
          user && user.createdAt > IOS_APP_RELEASE_DATE;
        return {
          wizardFooterButtonProps: {
            label: userSeenOnboarding
              ? translate('Next')
              : translate('Update now'),
          },
        };
      }
      default:
        return assertUnreachable(step);
    }
  }, [firstName, step]);
}
export default useStepData;
